import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const STALE_THRESHOLD_MS = 60 * 1000; // 1 minute
const SYNC_DEBOUNCE_MS = 5000; // 5 seconds debounce between syncs

interface DynamicTag {
  id: string;
  name: string;
  tag_type: "dynamic" | "manual";
  last_synced_at: string | null;
}

/**
 * Hook that auto-syncs stale dynamic tags when mounted.
 * Uses a "stale-while-revalidate" pattern - data is shown immediately,
 * and stale dynamic tags are synced in the background.
 */
export function useDynamicTagsAutoSync() {
  const queryClient = useQueryClient();
  const syncingRef = useRef<Set<string>>(new Set());
  const lastSyncAttemptRef = useRef<number>(0);

  // Fetch all dynamic tags with their last_synced_at
  const { data: dynamicTags = [] } = useQuery({
    queryKey: ["dynamic-tags-sync-check"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_tags")
        .select("id, name, tag_type, last_synced_at")
        .eq("tag_type", "dynamic");

      if (error) {
        console.error("Failed to fetch dynamic tags for sync check:", error);
        return [];
      }
      return data as DynamicTag[];
    },
    staleTime: 30000, // Consider fresh for 30 seconds
  });

  // Check for stale tags and sync them
  useEffect(() => {
    const syncStaleTags = async () => {
      const now = Date.now();

      // Debounce sync attempts
      if (now - lastSyncAttemptRef.current < SYNC_DEBOUNCE_MS) {
        return;
      }

      const staleTags = dynamicTags.filter((tag) => {
        // Skip if already syncing
        if (syncingRef.current.has(tag.id)) return false;

        // Check if stale (never synced or older than threshold)
        if (!tag.last_synced_at) return true;
        const lastSynced = new Date(tag.last_synced_at).getTime();
        return now - lastSynced > STALE_THRESHOLD_MS;
      });

      if (staleTags.length === 0) return;

      lastSyncAttemptRef.current = now;

      // Sync each stale tag in the background
      for (const tag of staleTags) {
        syncingRef.current.add(tag.id);

        try {
          console.log(`Auto-syncing stale dynamic tag: ${tag.name}`);
          const { error } = await supabase.functions.invoke("admin-user-actions", {
            body: {
              action: "sync_all_tag_rules",
              data: { tag_id: tag.id },
            },
          });

          if (error) {
            console.error(`Failed to auto-sync tag ${tag.name}:`, error);
          }
        } catch (err) {
          console.error(`Failed to auto-sync tag ${tag.name}:`, err);
        } finally {
          syncingRef.current.delete(tag.id);
        }
      }

      // Invalidate queries to refresh data after sync
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      queryClient.invalidateQueries({ queryKey: ["user-tags"] });
      queryClient.invalidateQueries({ queryKey: ["user-tag-assignments"] });
      queryClient.invalidateQueries({ queryKey: ["dynamic-tags-sync-check"] });
    };

    syncStaleTags();
  }, [dynamicTags, queryClient]);

  return {
    dynamicTagsCount: dynamicTags.length,
    isSyncing: syncingRef.current.size > 0,
  };
}
